<div class="panel-shell">
  <!-- Header -->
  <div class="panel-header">
    <app-heading level="h3" class="panel-title">Notifications</app-heading>
    <div class="flex items-center gap-1">
      @if (tab() === 'all' && notifications.unreadCount() > 0) {
      <app-button
        variant="ghost"
        size="sm"
        icon="check-check"
        [loading]="markingAll()"
        loadingText="Marking…"
        (click)="markAllRead()"
      >
        Mark all read
      </app-button>
      }
      <app-button
        variant="ghost"
        size="sm"
        [iconOnly]="true"
        icon="settings"
        title="Notification preferences"
        (click)="goToPreferences()"
      />
      @if (mobile()) {
      <app-button
        variant="ghost"
        size="sm"
        [iconOnly]="true"
        icon="x"
        title="Close"
        (click)="closed.emit()"
      />
      }
    </div>
  </div>

  <!-- Soft-ask for browser push -->
  @if (webPush.showSoftAsk()) {
  <div class="soft-ask" animate.enter="hv-banner-enter" animate.leave="hv-banner-leave">
    <div class="soft-ask-icon">
      <lucide-icon name="bell-ring" [size]="18"></lucide-icon>
    </div>
    <div class="flex-1 min-w-0">
      <app-text size="sm" weight="medium">Turn on browser alerts</app-text>
      <app-text size="xs" color="muted">
        Get alerts for critical events even when this tab is in the background.
      </app-text>
      <div class="flex items-center gap-2 mt-2">
        <app-button size="xs" variant="primary" [loading]="webPush.enabling()" loadingText="Enabling…" (click)="enablePush()">
          Enable
        </app-button>
        <app-button size="xs" variant="ghost" (click)="webPush.dismissSoftAsk()">Not now</app-button>
      </div>
    </div>
  </div>
  }

  <!-- Tabs -->
  <div class="panel-tabs">
    <app-button-group
      [options]="tabOptions()"
      [ngModel]="tab()"
      (ngModelChange)="tab.set($event)"
      size="sm"
    />
  </div>

  <!-- Body -->
  <div class="panel-body" role="list">
    @if (tab() === 'needs') {
      <!-- Severity chips -->
      @if (!notifications.needsActionLoading() && notifications.needsActionCount() > 0) {
      <div class="severity-chips" role="group" aria-label="Filter by severity">
        @for (s of severities; track s) {
        @if (notifications.needsActionBySeverity()[s] > 0) {
        <div
          class="severity-chip"
          [class]="'sev-' + s"
          [class.is-active]="severityFilter() === s"
          role="button"
          tabindex="0"
          [attr.aria-pressed]="severityFilter() === s"
          (click)="toggleSeverity(s)"
          (keydown.enter)="toggleSeverity(s)"
          (keydown.space)="$event.preventDefault(); toggleSeverity(s)"
        >
          <lucide-icon [name]="severityIcons[s]" [size]="12"></lucide-icon>
          <span>{{ notifications.needsActionBySeverity()[s] }} {{ severityLabels[s] }}</span>
        </div>
        }
        }
      </div>
      }

      @if (notifications.needsActionLoading() && pendingInvites().length === 0) {
        @for (i of skeletonRows; track i) {
        <div class="row skeleton-row" aria-hidden="true">
          <div class="sk sk-dot"></div>
          <div class="flex-1 flex flex-col gap-2">
            <div class="sk sk-line w-3/5"></div>
            <div class="sk sk-line w-full"></div>
            <div class="sk sk-line w-1/3"></div>
          </div>
        </div>
        }
      } @else if (needsAction().length === 0 && pendingInvites().length === 0) {
      <div class="empty-state">
        <div class="empty-icon"><lucide-icon name="check-check" [size]="22"></lucide-icon></div>
        <app-text size="base" weight="semibold">You're all caught up</app-text>
        <app-text size="sm" color="muted" class="max-w-[280px] text-center">
          Anything that needs a dispatcher, payroll or compliance decision will show up here — claim it so the team knows you're on it.
        </app-text>
        <app-button variant="link" size="sm" (click)="goToPreferences()">Notification preferences</app-button>
      </div>
      } @else {
        @for (inv of pendingInvites(); track inv.id) {
        <div
          class="row sev-action"
          role="listitem"
          animate.enter="hv-item-enter"
          animate.leave="hv-item-leave"
          [style.--stagger-idx]="$index"
        >
          <div class="sev-icon sev-action" aria-label="Team Invitation">
            <lucide-icon name="mail-plus" [size]="16"></lucide-icon>
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-start justify-between gap-2">
              <app-text size="sm" weight="semibold" class="row-title">
                Invitation to join {{ inv.companyName }}
              </app-text>
              @if (inv.createdAt) {
              <app-text size="xs" color="muted" class="shrink-0 whitespace-nowrap" [attr.title]="absoluteTime(inv.createdAt)">
                {{ inv.createdAt | timeAgo }}
              </app-text>
              }
            </div>
            <app-text size="sm" color="muted" class="row-body">
              {{ inv.invitedByName }} invited you to join as {{ formatInviteRoles(inv) }}.
            </app-text>
            <div class="actions">
              <app-button
                size="xs"
                variant="primary"
                icon="check"
                [loading]="isBusy('invite:' + inv.id)"
                [disabled]="isBusy('invite:' + inv.id)"
                loadingText="Joining…"
                (click)="acceptInvite(inv)"
              >
                Accept
              </app-button>
              <app-button
                size="xs"
                variant="ghost"
                [disabled]="isBusy('invite:' + inv.id)"
                (click)="declineInvite(inv)"
              >
                Decline
              </app-button>
            </div>
          </div>
        </div>
        }

        @for (item of needsAction(); track item.id) {
        <div
          class="row"
          [class]="'sev-' + item.severity"
          role="listitem"
          animate.enter="hv-item-enter"
          animate.leave="hv-item-leave"
          [style.--stagger-idx]="$index"
        >
          <div class="sev-icon" [class]="'sev-' + item.severity" [attr.aria-label]="severityLabels[item.severity]">
            <lucide-icon [name]="severityIcons[item.severity]" [size]="16"></lucide-icon>
          </div>

          <div class="flex-1 min-w-0">
            <div
              class="row-main"
              [class.is-link]="!!item.deepLink"
              [attr.role]="item.deepLink ? 'link' : null"
              [attr.tabindex]="item.deepLink ? 0 : null"
              (click)="openNeedsAction(item)"
              (keydown.enter)="openNeedsAction(item)"
            >
              <div class="flex items-start justify-between gap-2">
                <app-text size="sm" weight="semibold" class="row-title">{{ item.displayTitle }}</app-text>
                <app-text size="xs" color="muted" class="shrink-0 whitespace-nowrap" [attr.title]="absoluteTime(item.createdAt)">
                  {{ item.createdAt | timeAgo }}
                </app-text>
              </div>
              @if (item.displayBody) {
              <app-text size="sm" color="muted" class="row-body">{{ item.displayBody }}</app-text>
              }
            </div>

            <div class="chips">
              @if (item.isYourLoad) {
              <span class="chip chip-primary"><lucide-icon name="star" [size]="11"></lucide-icon>Your load</span>
              }
              @if (item.loadId) {
              <span class="chip"><lucide-icon name="package" [size]="11"></lucide-icon>{{ chipLabel(item.args, ['loadNumber', 'loadRef', 'referenceNumber']) || 'Load' }}</span>
              }
              @if (item.driverId) {
              <span class="chip"><lucide-icon name="user-round" [size]="11"></lucide-icon>{{ chipLabel(item.args, ['driverName', 'driver']) || 'Driver' }}</span>
              }
              @if (item.claimedBy && !item.isClaimedByMe) {
              <span class="chip chip-muted"><lucide-icon name="hand" [size]="11"></lucide-icon>{{ item.claimedByName || 'Someone' }} is handling</span>
              }
              @if (item.isClaimedByMe) {
              <span class="chip chip-primary"><lucide-icon name="hand" [size]="11"></lucide-icon>You're handling</span>
              }
              @if (item.isReadOnly) {
              <span class="chip chip-muted"><lucide-icon name="eye" [size]="11"></lucide-icon>View only</span>
              }
            </div>

            @if (!item.isReadOnly && item.actionable !== false) {
              @if (resolvingId() === item.id) {
              <div class="resolve-box" animate.enter="hv-banner-enter" animate.leave="hv-banner-leave">
                <app-input
                  size="sm"
                  placeholder="Add a note (optional)"
                  [value]="resolveNote()"
                  (valueChange)="resolveNote.set($event || '')"
                  (keydown.enter)="confirmResolve(item)"
                  (keydown.escape)="$event.stopPropagation(); cancelResolve()"
                />
                <div class="flex items-center gap-2">
                  <app-button size="xs" variant="success" icon="check" [loading]="isBusy(item.id)" loadingText="Resolving…" (click)="confirmResolve(item)">
                    Resolve
                  </app-button>
                  <app-button size="xs" variant="ghost" (click)="cancelResolve()">Cancel</app-button>
                </div>
              </div>
              } @else {
              <div class="actions">
                @if (!item.claimedBy) {
                <app-button size="xs" variant="secondary" icon="hand" [disabled]="isBusy(item.id)" (click)="claim(item)">Claim</app-button>
                } @else if (item.isClaimedByMe) {
                <app-button size="xs" variant="ghost" icon="undo-2" [disabled]="isBusy(item.id)" (click)="claim(item, true)">Release</app-button>
                }
                <app-button size="xs" variant="outline" icon="check" [disabled]="isBusy(item.id)" (click)="startResolve(item)">Resolve</app-button>
              </div>
              }
            }
          </div>
        </div>
        }
      }
    } @else {
      <!-- All (personal inbox) -->
      @if (notifications.inboxLoading() && pendingInvites().length === 0) {
        @for (i of skeletonRows; track i) {
        <div class="row skeleton-row" aria-hidden="true">
          <div class="sk sk-dot"></div>
          <div class="flex-1 flex flex-col gap-2">
            <div class="sk sk-line w-2/3"></div>
            <div class="sk sk-line w-full"></div>
          </div>
        </div>
        }
      } @else if (inbox().length === 0 && pendingInvites().length === 0) {
      <div class="empty-state">
        <div class="empty-icon"><lucide-icon name="inbox" [size]="22"></lucide-icon></div>
        <app-text size="base" weight="semibold">No notifications yet</app-text>
        <app-text size="sm" color="muted" class="max-w-[280px] text-center">
          Load updates, driver activity and team changes you're subscribed to will appear here.
        </app-text>
        <app-button variant="link" size="sm" (click)="goToPreferences()">Notification preferences</app-button>
      </div>
      } @else {
        @for (inv of pendingInvites(); track inv.id) {
        <div
          class="row inbox-row is-unread"
          role="listitem"
          animate.enter="hv-item-enter"
          animate.leave="hv-item-leave"
          [style.--stagger-idx]="$index"
        >
          <div class="sev-icon sev-action">
            <lucide-icon name="mail-plus" [size]="16"></lucide-icon>
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-start justify-between gap-2">
              <app-text size="sm" weight="semibold" class="row-title">
                Invitation to join {{ inv.companyName }}
              </app-text>
              @if (inv.createdAt) {
              <app-text size="xs" color="muted" class="whitespace-nowrap" [attr.title]="absoluteTime(inv.createdAt)">
                {{ inv.createdAt | timeAgo }}
              </app-text>
              }
            </div>
            <app-text size="sm" color="muted" class="row-body">
              {{ inv.invitedByName }} invited you to join as {{ formatInviteRoles(inv) }}.
            </app-text>
            <div class="actions">
              <app-button
                size="xs"
                variant="primary"
                icon="check"
                [loading]="isBusy('invite:' + inv.id)"
                [disabled]="isBusy('invite:' + inv.id)"
                loadingText="Joining…"
                (click)="acceptInvite(inv)"
              >
                Accept
              </app-button>
              <app-button
                size="xs"
                variant="ghost"
                [disabled]="isBusy('invite:' + inv.id)"
                (click)="declineInvite(inv)"
              >
                Decline
              </app-button>
            </div>
          </div>
        </div>
        }

        @for (entry of inbox(); track entry.id) {
        <div
          class="row inbox-row is-link"
          [class.is-unread]="!entry.read"
          role="listitem"
          tabindex="0"
          animate.enter="hv-item-enter"
          animate.leave="hv-item-leave"
          [style.--stagger-idx]="$index"
          (click)="openInbox(entry)"
          (keydown.enter)="openInbox(entry)"
        >
          <div class="sev-icon" [class]="'sev-' + entry.severity">
            <lucide-icon [name]="severityIcons[entry.severity]" [size]="16"></lucide-icon>
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-start justify-between gap-2">
              <app-text size="sm" [weight]="entry.read ? 'medium' : 'semibold'" class="row-title">{{ entry.title }}</app-text>
              <div class="flex items-center gap-2 shrink-0">
                <app-text size="xs" color="muted" class="whitespace-nowrap" [attr.title]="absoluteTime(entry.createdAt)">
                  {{ entry.createdAt | timeAgo }}
                </app-text>
                @if (!entry.read) {
                <span class="unread-dot" aria-label="Unread"></span>
                }
              </div>
            </div>
            @if (entry.body) {
            <app-text size="sm" color="muted" class="row-body">{{ entry.body }}</app-text>
            }
            @if (entry.actionable && entry.status === 'resolved') {
            <div class="chips">
              <span class="chip chip-muted"><lucide-icon name="circle-check" [size]="11"></lucide-icon>Resolved{{ entry.resolvedByName ? ' by ' + entry.resolvedByName : '' }}</span>
            </div>
            }
          </div>
        </div>
        }
      }
    }
  </div>

  <!-- Footer -->
  <div class="panel-footer">
    <app-button variant="ghost" size="sm" suffixIcon="arrow-right" class="w-full" (click)="viewAll()">
      View all notifications
    </app-button>
  </div>
</div>
